import { useState, useRef, useCallback } from 'react';
import {
  Card, Button, Form, Input, DatePicker, TimePicker, Modal, Empty,
  Typography, Space, Tag, App, Divider,
} from 'antd';
import {
  EditOutlined, PrinterOutlined, EyeOutlined, ArrowLeftOutlined,
  CheckCircleOutlined, CalendarOutlined, EnvironmentOutlined,
  UserOutlined, MessageOutlined, QrcodeOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import TEMPLATES from './index';
import { CARD_W_MM, CARD_H_MM, CARD_W_PX, CARD_H_PX } from './cardSize';

const { Title, Text } = Typography;
const P = '#5F33E1';

// Fixed-size frame that scales an invitation card down to `width` px while
// keeping its real aspect ratio — avoids the old bug where the reserved
// layout box didn't match the actual scaled card size, causing overlap.
const Thumbnail = ({ width, children }) => {
  const scale = width / CARD_W_PX;
  const height = CARD_H_PX * scale;
  return (
    <div style={{ width, height, overflow: 'hidden', position: 'relative', flexShrink: 0 }}>
      <div style={{ transform: `scale(${scale})`, transformOrigin: 'top left', position: 'absolute', top: 0, left: 0 }}>
        {children}
      </div>
    </div>
  );
};

const InvitationBuilder = () => {
  const { message } = App.useApp();

  const [step, setStep] = useState('select'); // 'select' | 'form' | 'preview'
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [invData, setInvData] = useState(null);
  const [savedInvitations, setSavedInvitations] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('wp_invitations') || '[]');
    } catch { return []; }
  });

  const [form] = Form.useForm();
  const printRef = useRef(null);

  // Save to localStorage
  const saveInvitations = useCallback((list) => {
    setSavedInvitations(list);
    localStorage.setItem('wp_invitations', JSON.stringify(list));
  }, []);

  // Select template
  const handleSelectTemplate = (tpl) => {
    setSelectedTemplate(tpl);
    setStep('form');
    form.resetFields();
  };

  // Form submit → preview
  const handleFormSubmit = (values) => {
    const data = {
      groomName: values.groomName,
      brideName: values.brideName,
      date: values.date ? dayjs(values.date).format('DD MMMM, YYYY') : '',
      time: values.time ? dayjs(values.time).format('HH:mm') : '',
      venue: values.venue || '',
      address: values.address || '',
      message: values.message || '',
      // QR value = RSVP confirmation link
      qrValue: `${window.location.origin}/rsvp/${Date.now()}`,
    };
    setInvData(data);
    setStep('preview');

    // Save to localStorage
    const inv = {
      id: Date.now(),
      templateId: selectedTemplate.id,
      data,
      createdAt: new Date().toISOString(),
      confirmed: false,
    };
    const updated = [inv, ...savedInvitations];
    saveInvitations(updated);
    message.success('Taklifnoma yaratildi!');
  };

  // Toggle confirmation
  const handleToggleConfirm = (id) => {
    const updated = savedInvitations.map(inv =>
      inv.id === id ? { ...inv, confirmed: !inv.confirmed } : inv
    );
    saveInvitations(updated);
  };

  // Delete
  const handleDelete = (id) => {
    Modal.confirm({
      title: "O'chirish",
      content: "Bu taklifnomani o'chirishni xohlaysizmi?",
      okText: "Ha",
      cancelText: "Yo'q",
      okButtonProps: { danger: true },
      onOk: () => {
        const updated = savedInvitations.filter(inv => inv.id !== id);
        saveInvitations(updated);
        message.success("O'chirildi");
      },
    });
  };

  // Print handler — fits visa cards onto A4 maximally
  const handlePrint = () => {
    const el = printRef.current;
    if (!el) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      message.error('Pop-up bloklangan. Iltimos, pop-up ga ruxsat bering.');
      return;
    }

    // Calculate how many cards fit on A4 (210mm x 297mm) with margins
    const pageW = 190; // usable width in mm (210 - 2*10mm margin)
    const pageH = 277; // usable height (297 - 2*10mm margin)
    const cols = Math.floor(pageW / CARD_W_MM);
    const rows = Math.floor(pageH / CARD_H_MM);
    const gap = 3; // mm gap between cards

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Taklifnoma — Chop etish</title>
        <style>
          @page { size: A4; margin: 10mm; }
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body {
            display: flex;
            flex-wrap: wrap;
            gap: ${gap}mm;
            align-content: flex-start;
            font-family: 'Segoe UI', sans-serif;
          }
          .inv-card {
            break-inside: avoid;
            page-break-inside: avoid;
          }
        </style>
      </head>
      <body>
        ${Array(cols * rows).fill(el.innerHTML).join('')}
      </body>
      </html>
    `);
    printWindow.document.close();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };

  // ── STEP 1: Template selection ──
  const renderSelectStep = () => (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Title level={5} style={{ margin: 0 }}>Shablon tanlang</Title>
        <Text type="secondary" style={{ fontSize: 13 }}>
          Taklifnoma dizaynini tanlang, keyin ma'lumotlarni kiriting
        </Text>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
        gap: 14,
      }}>
        {TEMPLATES.map(tpl => {
          const Comp = tpl.component;
          return (
            <div
              key={tpl.id}
              onClick={() => handleSelectTemplate(tpl)}
              style={{
                background: 'var(--qk-card-bg)', borderRadius: 14, border: '1px solid var(--qk-border)',
                padding: 14, cursor: 'pointer', transition: 'all 0.25s',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.borderColor = tpl.color;
                e.currentTarget.style.boxShadow = `0 4px 20px ${tpl.color}18`;
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.borderColor = '#f0f0f0';
                e.currentTarget.style.boxShadow = 'none';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              {/* Preview card (scaled down to fit the grid cell) */}
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}>
                <Thumbnail width={220}>
                  <Comp data={{
                    groomName: 'Jasur',
                    brideName: 'Madina',
                    date: '15 Sentyabr, 2026',
                    time: '18:00',
                    venue: 'Navruz Saroyi',
                    address: 'Toshkent sh., Chilonzor t.',
                    message: 'Sizni kutib qolamiz!',
                    qrValue: 'https://example.com/rsvp/demo',
                  }} />
                </Thumbnail>
              </div>
              <div style={{ textAlign: 'center' }}>
                <Text strong style={{ fontSize: 14, display: 'block' }}>{tpl.preview} {tpl.name}</Text>
                <Text type="secondary" style={{ fontSize: 12 }}>{tpl.description}</Text>
              </div>
            </div>
          );
        })}
      </div>

      {/* Saved invitations */}
      {savedInvitations.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <Divider />
          <Title level={5}>Saqlangan taklifnomalar</Title>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {savedInvitations.map(inv => {
              const tpl = TEMPLATES.find(t => t.id === inv.templateId);
              if (!tpl) return null;
              const Comp = tpl.component;
              return (
                <div key={inv.id} style={{
                  display: 'flex', gap: 14, alignItems: 'center',
                  background: 'var(--qk-card-bg)', borderRadius: 12, padding: '10px 14px',
                  border: '1px solid var(--qk-border)',
                }}>
                  <Thumbnail width={150}>
                    <Comp data={inv.data} />
                  </Thumbnail>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Text strong style={{ fontSize: 13 }}>
                      {inv.data.groomName} & {inv.data.brideName}
                    </Text>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
                      <Tag color={tpl.color} style={{ borderRadius: 8, fontSize: 11 }}>{tpl.name}</Tag>
                      {inv.data.date && (
                        <Text type="secondary" style={{ fontSize: 11 }}>
                          <CalendarOutlined style={{ marginRight: 3 }} />{inv.data.date}
                        </Text>
                      )}
                      <Tag
                        color={inv.confirmed ? 'success' : 'warning'}
                        style={{ borderRadius: 8, fontSize: 11 }}
                        icon={inv.confirmed ? <CheckCircleOutlined /> : null}
                      >
                        {inv.confirmed ? 'Tasdiqlangan' : 'Kutilmoqda'}
                      </Tag>
                    </div>
                  </div>
                  <Space size={4}>
                    <Button
                      size="small"
                      icon={<CheckCircleOutlined />}
                      onClick={() => handleToggleConfirm(inv.id)}
                      style={{
                        borderRadius: 8,
                        color: inv.confirmed ? '#52c41a' : '#faad14',
                        borderColor: inv.confirmed ? '#52c41a' : '#faad14',
                      }}
                    >
                      {inv.confirmed ? 'Bekor' : 'Tasdiqlash'}
                    </Button>
                    <Button
                      size="small"
                      icon={<EyeOutlined />}
                      style={{ borderRadius: 8, borderColor: P, color: P }}
                      onClick={() => {
                        const t = TEMPLATES.find(x => x.id === inv.templateId);
                        if (t) {
                          setSelectedTemplate(t);
                          setInvData(inv.data);
                          setStep('preview');
                        }
                      }}
                    />
                    <Button
                      size="small"
                      danger
                      onClick={() => handleDelete(inv.id)}
                      style={{ borderRadius: 8 }}
                    >
                      ✕
                    </Button>
                  </Space>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );

  // ── STEP 2: Fill form ──
  const renderFormStep = () => {
    if (!selectedTemplate) return null;
    const Comp = selectedTemplate.component;

    return (
      <div>
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => setStep('select')}
          style={{ marginBottom: 12, color: P, fontWeight: 600 }}
        >
          Orqaga
        </Button>

        <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
          {/* Form */}
          <div style={{ flex: 1, minWidth: 280 }}>
            <Card style={{ borderRadius: 14 }} styles={{ body: { padding: '20px 24px' } }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                <EditOutlined style={{ color: P, fontSize: 16 }} />
                <Text strong style={{ fontSize: 15 }}>Ma'lumotlarni kiriting</Text>
              </div>

              <Form
                form={form}
                layout="vertical"
                onFinish={handleFormSubmit}
                requiredMark={false}
              >
                <Form.Item
                  name="groomName"
                  label="Kuyov ismi"
                  rules={[{ required: true, message: 'Kiriting' }]}
                >
                  <Input
                    prefix={<UserOutlined style={{ color: '#bbb' }} />}
                    placeholder="Masalan: Jasur"
                    style={{ borderRadius: 10 }}
                  />
                </Form.Item>

                <Form.Item
                  name="brideName"
                  label="Kelin ismi"
                  rules={[{ required: true, message: 'Kiriting' }]}
                >
                  <Input
                    prefix={<UserOutlined style={{ color: '#bbb' }} />}
                    placeholder="Masalan: Madina"
                    style={{ borderRadius: 10 }}
                  />
                </Form.Item>

                <div style={{ display: 'flex', gap: 12 }}>
                  <Form.Item
                    name="date"
                    label="Sana"
                    rules={[{ required: true, message: 'Tanlang' }]}
                    style={{ flex: 1 }}
                  >
                    <DatePicker
                      style={{ width: '100%', borderRadius: 10 }}
                      format="DD.MM.YYYY"
                      placeholder="Sanani tanlang"
                    />
                  </Form.Item>

                  <Form.Item
                    name="time"
                    label="Vaqt"
                    rules={[{ required: true, message: 'Tanlang' }]}
                    style={{ flex: 1 }}
                  >
                    <TimePicker
                      style={{ width: '100%', borderRadius: 10 }}
                      format="HH:mm"
                      placeholder="Vaqtni tanlang"
                      minuteStep={15}
                    />
                  </Form.Item>
                </div>

                <Form.Item
                  name="venue"
                  label="To'yxona nomi"
                  rules={[{ required: true, message: 'Kiriting' }]}
                >
                  <Input
                    prefix={<EnvironmentOutlined style={{ color: '#bbb' }} />}
                    placeholder="Masalan: Navruz Saroyi"
                    style={{ borderRadius: 10 }}
                  />
                </Form.Item>

                <Form.Item name="address" label="Manzil">
                  <Input
                    prefix={<EnvironmentOutlined style={{ color: '#bbb' }} />}
                    placeholder="Masalan: Toshkent sh., Chilonzor t."
                    style={{ borderRadius: 10 }}
                  />
                </Form.Item>

                <Form.Item name="message" label="Qo'shimcha xabar">
                  <Input.TextArea
                    placeholder="Masalan: Sizni kutib qolamiz!"
                    rows={2}
                    style={{ borderRadius: 10 }}
                  />
                </Form.Item>

                <Button
                  type="primary"
                  htmlType="submit"
                  block
                  icon={<EyeOutlined />}
                  style={{
                    height: 44, borderRadius: 12, fontWeight: 700,
                    background: `linear-gradient(135deg, ${P}, #8B5CF6)`,
                    border: 'none',
                  }}
                >
                  Ko'rish va yaratish
                </Button>
              </Form>
            </Card>
          </div>

          {/* Live preview */}
          <div style={{ flexShrink: 0 }}>
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
              <EyeOutlined style={{ marginRight: 4 }} /> Jonli ko'rinish
            </Text>
            <Form.Item noStyle shouldUpdate>
              {() => {
                const values = form.getFieldsValue();
                return (
                  <Comp data={{
                    groomName: values.groomName || 'Kuyov',
                    brideName: values.brideName || 'Kelin',
                    date: values.date ? dayjs(values.date).format('DD MMMM, YYYY') : '',
                    time: values.time ? dayjs(values.time).format('HH:mm') : '',
                    venue: values.venue || '',
                    address: values.address || '',
                    message: values.message || '',
                    qrValue: 'https://example.com/rsvp/preview',
                  }} />
                );
              }}
            </Form.Item>
          </div>
        </div>
      </div>
    );
  };

  // ── STEP 3: Preview ──
  const renderPreviewStep = () => {
    if (!selectedTemplate || !invData) return null;
    const Comp = selectedTemplate.component;

    return (
      <div>
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => { setStep('select'); setInvData(null); }}
          style={{ marginBottom: 12, color: P, fontWeight: 600 }}
        >
          Orqaga
        </Button>

        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          <Title level={4} style={{ margin: 0 }}>
            Taklifnomangiz tayyor! 🎉
          </Title>
          <Text type="secondary">
            Chop etish tugmasi orqali A4 formatida chop eting
          </Text>
        </div>

        <div style={{
          display: 'flex', justifyContent: 'center', marginBottom: 20,
        }}>
          <div ref={printRef}>
            <Comp data={invData} />
          </div>
        </div>

        {/* Info summary */}
        <Card style={{ borderRadius: 14, maxWidth: 500, margin: '0 auto 20px' }} styles={{ body: { padding: '16px 20px' } }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px', fontSize: 13 }}>
            <div>
              <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>Kuyov</Text>
              <Text strong>{invData.groomName}</Text>
            </div>
            <div>
              <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>Kelin</Text>
              <Text strong>{invData.brideName}</Text>
            </div>
            <div>
              <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>Sana</Text>
              <Text strong>{invData.date}</Text>
            </div>
            <div>
              <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>Vaqt</Text>
              <Text strong>{invData.time}</Text>
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>Joy</Text>
              <Text strong>{invData.venue}</Text>
              {invData.address && <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>{invData.address}</Text>}
            </div>
            {invData.message && (
              <div style={{ gridColumn: '1 / -1' }}>
                <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>Xabar</Text>
                <Text italic>{invData.message}</Text>
              </div>
            )}
            <div style={{ gridColumn: '1 / -1' }}>
              <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>
                <QrcodeOutlined style={{ marginRight: 4 }} />QR kod
              </Text>
              <Text copyable style={{ fontSize: 11, wordBreak: 'break-all' }}>{invData.qrValue}</Text>
            </div>
          </div>
        </Card>

        {/* Actions */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: 12 }}>
          <Button
            type="primary"
            size="large"
            icon={<PrinterOutlined />}
            onClick={handlePrint}
            style={{
              height: 48, borderRadius: 12, fontWeight: 700,
              background: `linear-gradient(135deg, ${P}, #8B5CF6)`,
              border: 'none', paddingInline: 32,
            }}
          >
            Chop etish (A4)
          </Button>
          <Button
            size="large"
            icon={<EditOutlined />}
            onClick={() => setStep('form')}
            style={{ height: 48, borderRadius: 12, fontWeight: 600, borderColor: P, color: P }}
          >
            Tahrirlash
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div>
      {step === 'select' && renderSelectStep()}
      {step === 'form' && renderFormStep()}
      {step === 'preview' && renderPreviewStep()}
    </div>
  );
};

export default InvitationBuilder;
